import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Bell, ChevronDown, CircleUserRound, HelpCircle, LogOut, Search, Settings } from 'lucide-react';
import { api } from '@/api/client';
import type { MeInfo } from '@/api/client';

/** 未登录（API 401）时的演示身份，与仪表盘 mock 数据保持一致 */
const demoUser: MeInfo = {
  email: 'john.smith@contoso.com',
  displayName: 'John Smith',
  role: 'GlobalAdmin',
  mfaEnabled: true,
};

const roleLabel = (role?: string) => (role || 'GlobalAdmin').replace(/([a-z])([A-Z])/g, '$1 $2');

export function Topbar() {
  const { instance, accounts } = useMsal();
  const account = accounts[0];
  const msalUser: MeInfo | null = account
    ? {
        email: account.username,
        displayName: account.name || account.username.split('@')[0],
        role: 'GlobalAdmin',
        mfaEnabled: true,
      }
    : null;

  const [me, setMe] = useState<MeInfo>(msalUser ?? demoUser);
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // API 可用时取后端档案（含真实角色），否则保留 Entra 账户信息 / 演示身份
    api.me().then(setMe).catch(() => { /* 保留当前身份 */ });
  }, []);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const signOut = () => {
    sessionStorage.removeItem('access_token');
    sessionStorage.clear();
    instance.logoutRedirect({ account: account ?? undefined }).catch(() => {
      window.location.assign('/');
    });
  };

  return (
    <header className="h-14 px-5 bg-[#06172a]/95 border-b border-[#123150] flex items-center gap-4">
      <div className="flex-1 max-w-[410px] relative ml-auto">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#9aabc0]" />
        <input
          type="text"
          placeholder="Search"
          className="w-full bg-[#0b2035] border border-[#19395a] rounded-md pl-9 pr-4 py-1.5 text-[13px] placeholder:text-[#637891] focus:outline-none focus:border-[#2f6faf]"
        />
      </div>

      <div className="flex items-center gap-3">
        <button className="relative w-8 h-8 grid place-items-center rounded-full hover:bg-[#0b2035]">
          <Bell className="w-[18px] h-[18px] text-[#c5d0df]" />
          <span className="absolute top-0.5 right-0 min-w-3.5 h-3.5 px-1 grid place-items-center bg-[#ef4444] text-white text-[9px] font-bold rounded-full">3</span>
        </button>
        <button className="w-8 h-8 grid place-items-center rounded-full hover:bg-[#0b2035]">
          <HelpCircle className="w-[18px] h-[18px] text-[#c5d0df]" />
        </button>

        <div ref={menuRef} className="relative">
          <button
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-2.5 pl-4 border-l border-[#254260] hover:opacity-90"
          >
            <CircleUserRound className="w-8 h-8 text-[#c5d0df]" />
            <div className="text-right">
              <div className="text-[13px] font-semibold leading-tight">{me.displayName}</div>
              <div className="text-[11px] text-[#8fa1b8] leading-tight">{roleLabel(me.role)}</div>
            </div>
            <ChevronDown className={`w-3.5 h-3.5 text-[#8fa1b8] transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>

          {open && (
            <div className="absolute right-0 top-[calc(100%+10px)] z-50 w-56 rounded-md border border-[#19395a] bg-[#0a1f35] shadow-xl py-1.5 text-[13px]">
              <div className="px-3.5 py-2 border-b border-[#152e4a]">
                <div className="font-semibold text-[#e7eef6]">{me.displayName}</div>
                <div className="text-[11px] text-[#8fa1b8] truncate">{me.email}</div>
                <div className="text-[11px] text-[#58a6ff] mt-0.5">
                  {roleLabel(me.role)}{me.mfaEnabled ? ' · MFA enabled' : ''}
                </div>
              </div>
              <Link
                to="/settings"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-3.5 py-2 text-[#c5d0df] hover:bg-[#0e2942]"
              >
                <Settings className="w-4 h-4" /> Account settings
              </Link>
              <button
                onClick={signOut}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[#f0a8ab] hover:bg-[#0e2942] text-left"
              >
                <LogOut className="w-4 h-4" /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
